/**
 * Services — USP Recognition & Documentation.
 *
 * Loaded only on pages where this block renders. Two responsibilities:
 *  1. Reveal choreography — the left column (eyebrow/title/lead) blurs in,
 *     then the cards stagger in, then each card's green bar sweeps left→right.
 *     Re-fires the bar on hover. One-shot per section, skipped under
 *     prefers-reduced-motion.
 *  2. Image lightbox — clicking a document tile opens a full-viewport overlay
 *     with the source image. ESC, backdrop click or the close button dismiss.
 */
import './style.scss';
import { gsap } from 'gsap';

function openLightbox(src, alt) {
	const overlay = document.createElement('div');
	overlay.className = 'gv-usprec-lightbox';
	overlay.setAttribute('role', 'dialog');
	overlay.setAttribute('aria-modal', 'true');

	overlay.innerHTML = `
		<div class="gv-usprec-lightbox__frame">
			<button type="button" class="gv-usprec-lightbox__close" aria-label="Close">
				<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
					<path d="M6 6L18 18M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
				</svg>
			</button>
			<img src="${src}" alt="${alt || ''}" />
		</div>
	`;

	const prevOverflow = document.body.style.overflow;
	document.body.style.overflow = 'hidden';
	document.body.appendChild(overlay);

	requestAnimationFrame(() => overlay.setAttribute('data-open', 'true'));

	const close = () => {
		overlay.removeEventListener('click', onClick);
		document.removeEventListener('keydown', onKey);
		overlay.setAttribute('data-open', 'false');
		setTimeout(() => {
			overlay.remove();
			document.body.style.overflow = prevOverflow;
		}, 300);
	};

	function onClick(e) {
		if (e.target === overlay || e.target.closest('.gv-usprec-lightbox__close')) {
			close();
		}
	}
	function onKey(e) {
		if (e.key === 'Escape') close();
	}

	overlay.addEventListener('click', onClick);
	document.addEventListener('keydown', onKey);
}

function init() {
	const sections = document.querySelectorAll('[data-usprec]');
	if (!sections.length) return;

	const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	sections.forEach((section) => {
		// --- Lightbox wiring ---------------------------------------------
		section.querySelectorAll('[data-usprec-trigger]').forEach((btn) => {
			btn.addEventListener('click', () => {
				const src = btn.getAttribute('data-usprec-full') || '';
				const alt = btn.getAttribute('data-usprec-alt') || '';
				if (!src) return;
				openLightbox(src, alt);
			});
		});

		// --- Reveal choreography -----------------------------------------
		const headEls = Array.from(section.querySelectorAll('[data-usprec-anim]'));
		const cards   = Array.from(section.querySelectorAll('[data-usprec-card]'));
		const bars    = Array.from(section.querySelectorAll('[data-usprec-bar]'));
		if (!headEls.length && !cards.length) return;

		if (reduced) {
			[...headEls, ...cards].forEach((el) => {
				el.style.opacity = '1';
				el.style.filter = 'none';
				el.style.transform = 'none';
			});
			return;
		}

		gsap.set(headEls, { opacity: 0, y: 20, filter: 'blur(10px)' });
		gsap.set(cards,   { opacity: 0, y: 28, filter: 'blur(12px)' });
		gsap.set(bars,    { scaleX: 0 });

		const play = () => {
			const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

			headEls.forEach((el, i) => {
				tl.to(el, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.7 }, 0.1 + i * 0.12);
			});

			const cardsStart = Math.max(0.25, headEls.length * 0.12);
			tl.to(cards, {
				opacity: 1, y: 0, filter: 'blur(0px)',
				duration: 0.7, stagger: 0.08,
				clearProps: 'transform',
			}, cardsStart);

			tl.to(bars, {
				scaleX: 1, duration: 0.55, ease: 'power2.out', stagger: 0.08,
			}, cardsStart + 0.2);
		};

		// Re-fill a card's bar on hover.
		cards.forEach((card) => {
			const bar = card.querySelector('[data-usprec-bar]');
			if (!bar) return;
			card.addEventListener('mouseenter', () => {
				gsap.fromTo(
					bar,
					{ scaleX: 0 },
					{ scaleX: 1, duration: 0.5, ease: 'power2.out', overwrite: true }
				);
			});
		});

		const io = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					io.unobserve(entry.target);
					play();
				});
			},
			{ rootMargin: '0px 0px -15% 0px', threshold: 0.1 }
		);
		io.observe(section);
	});
}

if (document.readyState !== 'loading') init();
else document.addEventListener('DOMContentLoaded', init);
